import {
  Badge,
  Box,
  Button,
  Heading,
  Input,
  NativeSelect,
  Spinner,
  Table,
  Text,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CountryCode } from 'libphonenumber-js'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  CircleCheck,
  Copy,
} from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useDebouncedCallback } from '@mangovoice/core'
import type { ToastLike } from '@mangovoice/mango-ui'
import {
  Checkbox,
  copyToClipboard,
  ListToolbar,
  Modal,
  Toggle,
} from '@mangovoice/mango-ui'

import { CreatePhoneNumberModal } from '../components/CreatePhoneNumberModal'
import {
  getPhoneNumberTypeLabel,
  getPhoneNumberTypeSelectOptions,
} from '../constants'
import type { PhoneNumbersTranslateFn } from '../types/i18n.types'
import { text } from '../types/i18n.types'
import type {
  BulkUpdateStatusError,
  BulkUpdateStatusPayload,
  PhoneNumberCreatePayload,
  PhoneNumberListItem,
  PhoneNumbersApi,
  SelectOption,
} from '../types/phoneNumber.types'
import { ApiError } from '../utils/ApiError'
import { formatApiFieldErrors } from '../utils/formatApiErrors'
import { formatPhoneNumber } from '../utils/formatPhoneNumber'
import { getStatusColorPalette } from '../utils/statusColor'
import { getTypeColorPalette } from '../utils/typeColor'

type PageSize = number | 'All'

const ALL_LIMIT = 1500
const PAGE_SIZE_KEY = 'phoneNumbers.pageSize'
const SHOW_FILTERS_KEY = 'phoneNumbers.showFilters'
const SORT_KEY = 'phoneNumbers.sort'

type SortField =
  | 'name'
  | 'number'
  | 'type'
  | 'voice_provider__name'
  | 'sms_provider__name'
  | 'toll_free'
  | 'caller_identification'
  | 'call_endpoint'

interface SortState {
  field: SortField
  direction: 'asc' | 'desc'
}

const DEFAULT_SORT: SortState = { field: 'name', direction: 'asc' }

function loadSort(): SortState {
  try {
    const raw = localStorage.getItem(SORT_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed?.field && parsed?.direction) return parsed
    }
  } catch {
    // localStorage unavailable
  }
  return DEFAULT_SORT
}

function saveSort(sort: SortState): void {
  try {
    localStorage.setItem(SORT_KEY, JSON.stringify(sort))
  } catch {
    // localStorage unavailable
  }
}

function formatShortDate(iso?: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const SortIcon: React.FC<{ field: SortField; sort: SortState }> = ({
  field,
  sort,
}) => {
  if (sort.field !== field)
    return <ArrowUpDown size={12} style={{ opacity: 0.3 }} />
  return sort.direction === 'asc' ? (
    <ArrowUp size={12} />
  ) : (
    <ArrowDown size={12} />
  )
}

function loadShowFilters(): boolean {
  try {
    return localStorage.getItem(SHOW_FILTERS_KEY) === 'open'
  } catch {
    return false
  }
}

function saveShowFilters(open: boolean): void {
  try {
    if (open) {
      localStorage.setItem(SHOW_FILTERS_KEY, 'open')
    } else {
      localStorage.removeItem(SHOW_FILTERS_KEY)
    }
  } catch {
    // localStorage unavailable
  }
}

function loadPageSize(): PageSize {
  try {
    const raw = localStorage.getItem(PAGE_SIZE_KEY)
    if (raw === 'All') return 'All'
    const n = Number(raw)
    if ([10, 25, 50, 100].includes(n)) return n
  } catch {
    // localStorage unavailable (SSR, private browsing, etc.)
  }
  return 50
}

// -- Filter panel --

interface FilterState {
  status?: string
  type?: string
  voiceProvider?: string
  smsProvider?: string
  tollFree?: string
  showDeleted: boolean
}

const initialFilters: FilterState = {
  status: '',
  type: '',
  voiceProvider: '',
  smsProvider: '',
  tollFree: '',
  showDeleted: false,
}

const FilterPanel: React.FC<{
  filters: FilterState
  onChange: (filters: FilterState) => void
  statuses: SelectOption[]
  providers: SelectOption[]
  isStaff?: boolean
  t?: PhoneNumbersTranslateFn
}> = ({ filters, onChange, statuses, providers, isStaff, t }) => {
  const hasActiveFilters =
    !!filters.status ||
    !!filters.type ||
    !!filters.voiceProvider ||
    !!filters.smsProvider ||
    !!filters.tollFree ||
    filters.showDeleted

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      gap={3}
      alignItems="flex-end"
      py={3}
      px={1}
    >
      {statuses.length > 0 && (
        <Box minW="140px">
          <Text fontSize="xs" fontWeight="500" mb={1} color="fg.muted">
            {text(t, 'phoneNumbers.filters.status', 'Status')}
          </Text>
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              value={filters.status || ''}
              onChange={(e) => onChange({ ...filters, status: e.target.value })}
            >
              <option value="">
                {text(t, 'phoneNumbers.filters.allStatuses', 'All')}
              </option>
              {statuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Box>
      )}

      <Box minW="120px">
        <Text fontSize="xs" fontWeight="500" mb={1} color="fg.muted">
          {text(t, 'phoneNumbers.filters.type', 'Type')}
        </Text>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            value={filters.type || ''}
            onChange={(e) => onChange({ ...filters, type: e.target.value })}
          >
            <option value="">
              {text(t, 'phoneNumbers.filters.allTypes', 'All')}
            </option>
            {getPhoneNumberTypeSelectOptions(t).map((pt) => (
              <option key={pt.value} value={pt.value}>
                {pt.label}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Box>

      {providers.length > 0 && (
        <>
          <Box minW="140px">
            <Text fontSize="xs" fontWeight="500" mb={1} color="fg.muted">
              {text(t, 'phoneNumbers.filters.voiceProvider', 'Voice Provider')}
            </Text>
            <NativeSelect.Root size="sm">
              <NativeSelect.Field
                value={filters.voiceProvider || ''}
                onChange={(e) =>
                  onChange({ ...filters, voiceProvider: e.target.value })
                }
              >
                <option value="">
                  {text(t, 'phoneNumbers.filters.allProviders', 'All')}
                </option>
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Box>

          <Box minW="140px">
            <Text fontSize="xs" fontWeight="500" mb={1} color="fg.muted">
              {text(t, 'phoneNumbers.filters.smsProvider', 'SMS Provider')}
            </Text>
            <NativeSelect.Root size="sm">
              <NativeSelect.Field
                value={filters.smsProvider || ''}
                onChange={(e) =>
                  onChange({ ...filters, smsProvider: e.target.value })
                }
              >
                <option value="">
                  {text(t, 'phoneNumbers.filters.allProviders', 'All')}
                </option>
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Box>
        </>
      )}

      <Box minW="120px">
        <Text fontSize="xs" fontWeight="500" mb={1} color="fg.muted">
          {text(t, 'phoneNumbers.filters.tollFree', 'Toll Free')}
        </Text>
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            value={filters.tollFree || ''}
            onChange={(e) => onChange({ ...filters, tollFree: e.target.value })}
          >
            <option value="">
              {text(t, 'phoneNumbers.filters.any', 'Any')}
            </option>
            <option value="true">
              {text(t, 'phoneNumbers.list.yes', 'Yes')}
            </option>
            <option value="false">
              {text(t, 'phoneNumbers.list.no', 'No')}
            </option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Box>

      {isStaff && (
        <Box display="flex" alignItems="center" gap={2} pb={0.5}>
          <Toggle
            size="sm"
            labelProps={{ fontSize: 'xs' }}
            checked={filters.showDeleted}
            onCheckedChange={(e) =>
              onChange({ ...filters, showDeleted: e.checked })
            }
            display={'flex'}
            paddingBottom={2}
          >
            {text(t, 'phoneNumbers.filters.showDeleted', 'Show Deleted')}
          </Toggle>
        </Box>
      )}

      {hasActiveFilters && (
        <Button
          size="xs"
          variant="ghost"
          borderRadius="lg"
          onClick={() => onChange(initialFilters)}
          mb={0.5}
        >
          {text(t, 'phoneNumbers.filters.clearAll', 'Clear Filters')}
        </Button>
      )}
    </Box>
  )
}

// -- Bulk status update modal --

const BulkStatusModal: React.FC<{
  open: boolean
  selectedCount: number
  statuses: SelectOption[]
  onClose: () => void
  onConfirm: (statusId: number) => void
  isUpdating: boolean
  t?: PhoneNumbersTranslateFn
}> = ({ open, selectedCount, statuses, onClose, onConfirm, isUpdating, t }) => {
  const [statusId, setStatusId] = useState('')

  useEffect(() => {
    if (open) setStatusId('')
  }, [open])

  return (
    <Modal
      open={open}
      onOpenChange={(e) => {
        if (!e.open) onClose()
      }}
      title={text(t, 'phoneNumbers.bulk.title', 'Bulk Update Status')}
      maxWidth="sm"
      footer={
        <>
          <Button
            size="sm"
            variant="outline"
            borderRadius="lg"
            onClick={onClose}
            disabled={isUpdating}
          >
            {text(t, 'phoneNumbers.edit.cancel', 'Cancel')}
          </Button>
          <Button
            size="sm"
            colorPalette="brand"
            borderRadius="lg"
            disabled={!statusId || isUpdating}
            loading={isUpdating}
            onClick={() => onConfirm(Number(statusId))}
          >
            {text(t, 'phoneNumbers.bulk.apply', 'Apply')}
          </Button>
        </>
      }
    >
      <Text fontSize="sm" color="fg.muted" mb={4}>
        {text(
          t,
          'phoneNumbers.bulk.description',
          `Update status for ${selectedCount} selected phone number(s).`,
        )}
      </Text>
      <NativeSelect.Root size="sm">
        <NativeSelect.Field
          value={statusId}
          onChange={(e) => setStatusId(e.target.value)}
        >
          <option value="">
            {text(t, 'phoneNumbers.edit.selectStatus', '— Select Status —')}
          </option>
          {statuses.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
    </Modal>
  )
}

type BulkUpdateResultState = {
  errors: BulkUpdateStatusError[]
  successCount: number
  totalCount: number
}

const BulkUpdateResultsDialog: React.FC<{
  result: BulkUpdateResultState | null
  onClose: () => void
  t?: PhoneNumbersTranslateFn
}> = ({ result, onClose, t }) => {
  if (!result) return null
  const { errors, successCount, totalCount } = result
  // Defensive: if the API somehow returns 0 successes and 0 errors, treat as no-op
  if (successCount === 0 && errors.length === 0) return null
  const allSucceeded = errors.length === 0 && successCount > 0
  const allFailed = successCount === 0 && errors.length > 0

  const title = allSucceeded
    ? text(t, 'phoneNumbers.bulk.results.titleSuccess', 'Bulk Update Completed')
    : allFailed
      ? text(
          t,
          'phoneNumbers.bulk.results.titleAllFailed',
          'Bulk Update Failed',
        )
      : text(
          t,
          'phoneNumbers.bulk.results.titlePartial',
          'Bulk Update Partially Completed',
        )

  const summary = allSucceeded
    ? text(
        t,
        'phoneNumbers.bulk.results.summarySuccess',
        `Successfully updated ${successCount} phone number(s).`,
        { count: successCount },
      )
    : allFailed
      ? text(
          t,
          'phoneNumbers.bulk.results.summaryAllFailed',
          `All ${totalCount} phone number(s) failed to update.`,
          { count: totalCount },
        )
      : text(
          t,
          'phoneNumbers.bulk.results.summaryPartial',
          `${successCount} of ${totalCount} updated. ${errors.length} failed:`,
          { successCount, totalCount, failedCount: errors.length },
        )

  return (
    <Modal
      open
      onOpenChange={(e) => {
        if (!e.open) onClose()
      }}
      title={title}
      maxWidth="xl"
      footer={
        <>
          <Button
            colorPalette={'brand'}
            size="sm"
            borderRadius="lg"
            onClick={onClose}
          >
            {text(t, 'phoneNumbers.bulk.results.close', 'Close')}
          </Button>
        </>
      }
    >
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        mb={errors.length > 0 ? 4 : 0}
      >
        {allSucceeded && (
          <Box
            css={{ '& svg': { color: 'fg.success' } }}
            flexShrink={0}
            display="inline-flex"
          >
            <CircleCheck size={18} />
          </Box>
        )}
        <Text fontSize="sm">{summary}</Text>
      </Box>

      {errors.length > 0 && (
        <Box borderWidth="1px" borderRadius="md" overflow="auto" maxH="300px">
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>
                  {text(t, 'phoneNumbers.bulk.results.colNumber', 'Number')}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {text(
                    t,
                    'phoneNumbers.bulk.results.colStatus',
                    'Current Status',
                  )}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {text(t, 'phoneNumbers.bulk.results.colError', 'Error')}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {errors.map((err, idx) => (
                <Table.Row key={`${err.id}-${idx}`}>
                  <Table.Cell fontFamily="mono" fontSize="xs">
                    {formatPhoneNumber(err.number)}
                  </Table.Cell>
                  <Table.Cell>
                    <Badge size="sm" variant="subtle">
                      {err.current_status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell fontSize="xs" color="fg.error">
                    {err.error}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </Modal>
  )
}

// -- Sub-components --

const LoadingState: React.FC<{ t?: PhoneNumbersTranslateFn }> = ({ t }) => (
  <Box textAlign="center" py={12}>
    <Spinner size="lg" />
    <Text mt={4} color="fg.muted">
      {text(t, 'phoneNumbers.list.loading', 'Loading phone numbers...')}
    </Text>
  </Box>
)

const ErrorState: React.FC<{ t?: PhoneNumbersTranslateFn }> = ({ t }) => (
  <Box textAlign="center" py={12}>
    <Text color="fg.error">
      {text(t, 'phoneNumbers.list.error', 'Failed to load phone numbers')}
    </Text>
  </Box>
)

const EmptyState: React.FC<{
  hasSearch: boolean
  t?: PhoneNumbersTranslateFn
}> = ({ hasSearch, t }) => (
  <Box textAlign="center" py={12}>
    <Text color="fg.muted">
      {hasSearch
        ? text(
            t,
            'phoneNumbers.list.noResults',
            'No phone numbers match your search',
          )
        : text(
            t,
            'phoneNumbers.list.empty',
            'No phone numbers found for this PBX',
          )}
    </Text>
  </Box>
)

const PhoneNumberRow: React.FC<{
  pn: PhoneNumberListItem
  t?: PhoneNumbersTranslateFn
  onClick?: () => void
  selected?: boolean
  onSelectChange?: (checked: boolean) => void
  showCheckbox?: boolean
  onCopy?: (value: string) => void
}> = ({ pn, t, onClick, selected, onSelectChange, showCheckbox, onCopy }) => {
  const smsActive = !!pn.sms_activated_at
  const e911Verified = !!pn.e911_registered_at
  const formattedNumber = formatPhoneNumber(pn.number)

  const handleCopyClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    // Copy the raw digits (e.g. "4356734236"), matching legacy behavior.
    // Users pasting into dialers/CRMs expect unformatted input.
    onCopy?.(pn.number)
  }

  return (
    <Table.Row
      cursor={onClick ? 'pointer' : undefined}
      _hover={onClick ? { bg: 'bg.subtle' } : undefined}
      onClick={onClick}
      bg={selected ? 'blue.500/10' : undefined}
    >
      {showCheckbox && (
        <Table.Cell width="40px" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            size="sm"
            checked={selected}
            onCheckedChange={(e) => onSelectChange?.(!!e.checked)}
            inputProps={{
              'aria-label': text(
                t,
                'phoneNumbers.bulk.selectRow',
                'Select {{number}}',
                { number: formattedNumber },
              ),
            }}
          />
        </Table.Cell>
      )}
      <Table.Cell fontSize="sm">
        <Text fontWeight="500">{pn.name || '—'}</Text>
        <Box display="flex" flexDirection="column" gap={0.5} mt={0.5}>
          <Text color="fg.muted" fontSize="xs">
            Status:{' '}
            {pn.status ? (
              <Badge
                size="xs"
                variant="subtle"
                colorPalette={getStatusColorPalette(pn.status)}
              >
                {pn.status.display}
              </Badge>
            ) : (
              '—'
            )}
          </Text>
          <Text color="fg.muted" fontSize="xs">
            Type:{' '}
            {pn.type ? (
              <Badge
                size="xs"
                variant="subtle"
                colorPalette={getTypeColorPalette(pn.type)}
              >
                {getPhoneNumberTypeLabel(pn.type, t)}
              </Badge>
            ) : (
              '—'
            )}
          </Text>
        </Box>
      </Table.Cell>
      <Table.Cell fontSize="xs" fontFamily="mono">
        <Box display="flex" alignItems="center" gap={1}>
          <Text
            as="span"
            userSelect="text"
            onClick={(e) => e.stopPropagation()}
          >
            {formattedNumber}
          </Text>
          <Button
            size="xs"
            variant="ghost"
            aria-label={text(t, 'phoneNumbers.list.copyNumber', 'Copy Number')}
            title={text(t, 'phoneNumbers.list.copyNumber', 'Copy Number')}
            onClick={handleCopyClick}
            px={1}
            minW="auto"
            h="auto"
            py={1}
          >
            <Copy size={12} />
          </Button>
        </Box>
      </Table.Cell>
      <Table.Cell fontSize="xs">
        {pn.extension?.number ?? pn.call_endpoint ?? '—'}
      </Table.Cell>
      <Table.Cell fontSize="sm">
        {pn.caller_identification
          ? formatPhoneNumber(pn.caller_identification)
          : '—'}
      </Table.Cell>
      <Table.Cell fontSize="sm">
        {pn.toll_free ? (
          <Badge size="sm" colorPalette="success">
            {text(t, 'phoneNumbers.list.yes', 'Yes')}
          </Badge>
        ) : (
          <Text color="fg.muted">{text(t, 'phoneNumbers.list.no', 'No')}</Text>
        )}
      </Table.Cell>
      <Table.Cell fontSize="sm">
        {smsActive ? (
          <Badge size="sm" colorPalette="success">
            {text(t, 'phoneNumbers.list.smsActive', 'Active')}
          </Badge>
        ) : (
          <Text color="fg.muted">{text(t, 'phoneNumbers.list.no', 'No')}</Text>
        )}
      </Table.Cell>
      <Table.Cell fontSize="sm">
        <Text color="fg.muted" fontSize="sm">
          Voice: {pn.voice_provider ?? '—'}
        </Text>
        <Text color="fg.muted" fontSize="sm">
          SMS: {pn.sms_provider ?? '—'}
        </Text>
        <Text color="fg.muted" fontSize="sm">
          e911:{' '}
          {e911Verified ? (
            <Badge size="sm" colorPalette="success">
              {text(t, 'phoneNumbers.list.e911Verified', 'Verified')}
            </Badge>
          ) : (
            '—'
          )}
        </Text>
      </Table.Cell>
      <Table.Cell fontSize="sm" color="fg.muted">
        {formatShortDate(pn.updated_at)}
      </Table.Cell>
    </Table.Row>
  )
}

// -- Main page component --

export interface PhoneNumbersListPageProps {
  /** Phone Numbers API instance */
  api: PhoneNumbersApi
  /** PBX ID */
  pbxId: number
  /** Returns the path for a phone number detail page */
  getDetailPath?: (phoneNumberId: number) => string
  /** Called when a row is clicked (alternative to getDetailPath for custom navigation) */
  onRowClick?: (phoneNumber: PhoneNumberListItem) => void
  /** Optional translation function for i18n; when omitted, components use fallback English */
  t?: PhoneNumbersTranslateFn
  toast?: ToastLike
  /** Whether the current user is staff (gates staff-only filters) */
  isStaff?: boolean
  /** Whether the current user can create phone numbers */
  canCreate?: boolean
  /** Whether the current user can edit phone numbers (gates bulk update) */
  canEdit?: boolean
  /**
   * When true (default), the pagination bar shows a range summary (e.g. `1—10 of 461`).
   * Set false to hide it (uses `ListToolbar`’s `showRecordRange`).
   */
  showPaginationRecordRange?: boolean
  /** ISO 3166-1 alpha-2 country code (e.g. 'US', 'GB') used to pre-fill the dial code in the Create modal. */
  defaultCountryCode?: CountryCode
}

export const PhoneNumbersListPage: React.FC<PhoneNumbersListPageProps> = ({
  api,
  pbxId,
  getDetailPath,
  onRowClick,
  t,
  toast,
  isStaff,
  canCreate,
  canEdit,
  showPaginationRecordRange = true,
  defaultCountryCode,
}) => {
  const queryClient = useQueryClient()
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [pageSize, setPageSizeState] = useState<PageSize>(loadPageSize)
  const setPageSize = useCallback((size: PageSize) => {
    setPageSizeState(size)
    try {
      localStorage.setItem(PAGE_SIZE_KEY, String(size))
    } catch {
      // localStorage unavailable
    }
  }, [])
  const [currentPage, setCurrentPage] = useState(1)
  const [filters, setFilters] = useState<FilterState>(initialFilters)
  const [sort, setSortState] = useState<SortState>(loadSort)
  const setSort = useCallback(
    (updater: SortState | ((s: SortState) => SortState)) => {
      setSortState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater
        saveSort(next)
        return next
      })
    },
    [],
  )
  const handleSort = useCallback(
    (field: SortField) => {
      setSort((prev) => ({
        field,
        direction:
          prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
      }))
      setCurrentPage(1)
    },
    [setSort],
  )
  const [showFilters, setShowFiltersState] = useState(loadShowFilters)
  const setShowFilters = useCallback(
    (updater: boolean | ((v: boolean) => boolean)) => {
      setShowFiltersState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater
        saveShowFilters(next)
        return next
      })
    },
    [],
  )

  // Fetch select options for filters
  const { data: statusOptions = [] } = useQuery({
    queryKey: ['phoneNumberStatuses'],
    queryFn: async () => {
      if (!api.getStatuses) return []
      const res = await api.getStatuses()
      return res.ok && res.data ? res.data : []
    },
    staleTime: 5 * 60 * 1000,
  })

  const { data: providerOptions = [] } = useQuery({
    queryKey: ['phoneNumberProviders'],
    queryFn: async () => {
      if (!api.getProviders) return []
      const res = await api.getProviders()
      return res.ok && res.data ? res.data : []
    },
    staleTime: 5 * 60 * 1000,
  })

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false)
  const [bulkUpdateResult, setBulkUpdateResult] =
    useState<BulkUpdateResultState | null>(null)

  const canBulkUpdate = canEdit !== false && !!api.bulkUpdateStatus

  const toggleSelect = useCallback((id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }, [])

  const { data: extensionOptions = [] } = useQuery({
    queryKey: ['phoneNumberExtensions', pbxId],
    queryFn: async () => {
      if (!api.getExtensions) return []
      const res = await api.getExtensions(pbxId)
      return res.ok && res.data ? res.data : []
    },
    staleTime: 5 * 60 * 1000,
    enabled: isCreateOpen,
  })

  const { data: faxMachineOptions = [] } = useQuery({
    queryKey: ['phoneNumberFaxMachines', pbxId],
    queryFn: async () => {
      if (!api.getFaxMachines) return []
      const res = await api.getFaxMachines(pbxId)
      return res.ok && res.data ? res.data : []
    },
    staleTime: 5 * 60 * 1000,
    enabled: isCreateOpen,
  })

  const { data: integrationOptions = [] } = useQuery({
    queryKey: ['phoneNumberIntegrations', pbxId],
    queryFn: async () => {
      if (!api.getIntegrations) return []
      const res = await api.getIntegrations(pbxId)
      return res.ok && res.data ? res.data : []
    },
    staleTime: 5 * 60 * 1000,
    enabled: isCreateOpen,
  })

  const createMutation = useMutation({
    mutationFn: async (payload: PhoneNumberCreatePayload) => {
      const response = await api.createPhoneNumber(payload)
      if (!response.ok) {
        throw new ApiError('Failed to create phone number', response.errors)
      }
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phoneNumbers'] })
      toast?.create({
        title: text(
          t,
          'phoneNumbers.list.toast.createSuccess',
          'Phone number created',
        ),
        type: 'success',
      })
    },
    onError: (error: Error) => {
      toast?.create({
        title: text(
          t,
          'phoneNumbers.list.toast.createFailed',
          'Failed to create phone number',
        ),
        description:
          error instanceof ApiError
            ? formatApiFieldErrors(error.fieldErrors)
            : undefined,
        type: 'error',
      })
    },
  })

  const handleCreate = useCallback(
    async (payload: PhoneNumberCreatePayload) => {
      await createMutation.mutateAsync(payload)
    },
    [createMutation],
  )

  const handleCreateSuccess = useCallback(() => {
    setIsCreateOpen(false)
  }, [])

  const applySearch = useDebouncedCallback((value: string) => {
    setDebouncedSearch(value)
    setCurrentPage(1)
  }, 300)

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setSearchInput(value)
      applySearch(value)
    },
    [applySearch],
  )

  const activeFilterCount = useMemo(
    () =>
      [
        !!filters.status,
        !!filters.type,
        !!filters.voiceProvider,
        !!filters.smsProvider,
        !!filters.tollFree,
        filters.showDeleted,
      ].filter(Boolean).length,
    [filters],
  )

  const handleFilterChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters)
    setCurrentPage(1)
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [pageSize])

  const fetchAll = pageSize === 'All'
  const limit = fetchAll ? ALL_LIMIT : (pageSize as number)
  const offset = fetchAll ? 0 : (currentPage - 1) * (pageSize as number)

  const ordering = sort.direction === 'desc' ? `-${sort.field}` : sort.field

  // The backend's "Deleted" phone-number status (id=8) is effectively unused —
  // soft-deleting a number leaves its prior status in place (e.g. "Pending
  // Port"), so filtering by status=Deleted returns nothing. Treat picking that
  // option as "show me soft-deleted numbers" instead: drop the status filter
  // and force deleted=true.
  const deletedStatusId = useMemo(
    () => statusOptions.find((s) => s.name === 'Deleted')?.id,
    [statusOptions],
  )
  const isFilteringByDeletedStatus =
    !!filters.status &&
    deletedStatusId !== undefined &&
    Number(filters.status) === deletedStatusId

  const queryParams = useMemo(
    () => ({
      pbx_id: pbxId,
      limit,
      offset,
      search: debouncedSearch.trim() || undefined,
      ordering,
      status: isFilteringByDeletedStatus
        ? undefined
        : filters.status
          ? Number(filters.status)
          : undefined,
      type: filters.type || undefined,
      voice_provider: filters.voiceProvider
        ? Number(filters.voiceProvider)
        : undefined,
      sms_provider: filters.smsProvider
        ? Number(filters.smsProvider)
        : undefined,
      toll_free:
        filters.tollFree === 'true'
          ? true
          : filters.tollFree === 'false'
            ? false
            : undefined,
      deleted: filters.showDeleted || isFilteringByDeletedStatus || undefined,
    }),
    [
      pbxId,
      limit,
      offset,
      debouncedSearch,
      ordering,
      filters,
      isFilteringByDeletedStatus,
    ],
  )

  const { data, isLoading, error } = useQuery({
    queryKey: ['phoneNumbers', queryParams, fetchAll],
    queryFn: async () => {
      if (!pbxId) return { count: 0, results: [], next: null, previous: null }

      if (fetchAll) {
        let allResults: PhoneNumberListItem[] = []
        let currentOffset = 0
        let hasMore = true

        while (hasMore) {
          const response = await api.getPhoneNumbers({
            ...queryParams,
            limit: ALL_LIMIT,
            offset: currentOffset,
          })
          if (!response.ok || !response.data) {
            throw new Error('Failed to load phone numbers')
          }
          allResults = [...allResults, ...response.data.results]
          hasMore = !!response.data.next
          currentOffset += ALL_LIMIT
        }

        return {
          count: allResults.length,
          results: allResults,
          next: null,
          previous: null,
        }
      }

      const response = await api.getPhoneNumbers(queryParams)
      if (!response.ok || !response.data) {
        throw new Error('Failed to load phone numbers')
      }
      return response.data
    },
    retry: false,
  })

  const phoneNumbers = data?.results ?? []
  const totalCount = data?.count ?? 0

  // Capture server group from the first result that has it so the header
  // stays stable across page/sort/filter changes and empty result sets.
  // Reset per PBX because admin3 reuses this component across :pbxId routes.
  const serverGroupRef = useRef<{
    pbxId: number
    serverGroup: { id: number; name: string; host: string }
  } | null>(null)
  if (serverGroupRef.current?.pbxId !== pbxId) {
    const sg = phoneNumbers.find(
      (pn) => pn.pbx_id === pbxId && pn.pbx?.server_group,
    )?.pbx?.server_group
    serverGroupRef.current = sg ? { pbxId, serverGroup: sg } : null
  }
  const serverGroup = serverGroupRef.current?.serverGroup
  const serverGroupLabel = serverGroup
    ? serverGroup.name || serverGroup.host
    : undefined

  const toggleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedIds(new Set(phoneNumbers.map((pn) => pn.id)))
      } else {
        setSelectedIds(new Set())
      }
    },
    [phoneNumbers],
  )

  const allSelected =
    phoneNumbers.length > 0 &&
    phoneNumbers.every((pn) => selectedIds.has(pn.id))

  const bulkMutation = useMutation({
    mutationFn: async (payload: BulkUpdateStatusPayload) => {
      if (!api.bulkUpdateStatus) throw new Error('Not supported')
      const response = await api.bulkUpdateStatus(payload)
      const data = response.data
      if (!data) throw new Error('Failed to bulk update')
      return data
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['phoneNumbers'] })

      const successCount = result.success_count
      const errors = result.errors ?? []
      const totalCount = successCount + errors.length

      setIsBulkModalOpen(false)
      // Chakra v3 DialogRoot doesn't expose onExitComplete, so we delay to
      // let BulkStatusModal fully unmount before mounting the results dialog.
      // Ideally this would be driven by a state machine or exit callback.
      setTimeout(() => {
        setBulkUpdateResult({ errors, successCount, totalCount })
      }, 300)
    },
    onError: () => {
      toast?.create({
        title: text(
          t,
          'phoneNumbers.bulk.toast.failed',
          'Failed to bulk update status',
        ),
        type: 'error',
      })
    },
  })

  const handleBulkConfirm = useCallback(
    (statusId: number) => {
      bulkMutation.mutate({
        phone_number_ids: Array.from(selectedIds),
        status_id: statusId,
      })
    },
    [bulkMutation, selectedIds],
  )

  const handleCopyNumber = useCallback(
    (value: string) => {
      copyToClipboard(value)
        .then(() =>
          toast?.create({
            title: text(
              t,
              'phoneNumbers.list.toast.copied',
              'Number copied to clipboard',
            ),
            type: 'success',
          }),
        )
        .catch(() =>
          toast?.create({
            title: text(
              t,
              'phoneNumbers.list.toast.copyFailed',
              'Failed to copy number',
            ),
            type: 'error',
          }),
        )
    },
    [toast, t],
  )

  useEffect(() => {
    if (fetchAll || isLoading || totalCount <= 0) return
    const totalPages = Math.max(1, Math.ceil(totalCount / (pageSize as number)))
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, fetchAll, isLoading, pageSize, totalCount])

  // Clear selection when data changes (page, filters, search)
  useEffect(() => {
    setSelectedIds(new Set())
  }, [queryParams])

  const PaginationToolbar: React.FC<{ mb?: number }> = ({ mb }) => (
    <Box mb={mb}>
      <ListToolbar
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        currentPage={currentPage}
        totalCount={totalCount}
        onPageChange={setCurrentPage}
        isLoading={isLoading}
        showToolbar={totalCount > 0}
        showRecordRange={showPaginationRecordRange}
        t={t}
        i18nPrefix="phoneNumbers.pagination"
      />
    </Box>
  )

  const handleRowClick = useCallback(
    (pn: PhoneNumberListItem) => {
      if (onRowClick) {
        onRowClick(pn)
      } else if (getDetailPath) {
        window.location.href = getDetailPath(pn.id)
      }
    },
    [onRowClick, getDetailPath],
  )

  const renderContent = () => {
    if (isLoading) return <LoadingState t={t} />
    if (error) return <ErrorState t={t} />
    if (phoneNumbers.length === 0) {
      return <EmptyState hasSearch={!!debouncedSearch} t={t} />
    }
    return (
      <>
        {canBulkUpdate && selectedIds.size > 0 && (
          <Box
            display="flex"
            alignItems="center"
            gap={3}
            py={2}
            px={3}
            mb={3}
            bg="bg.info"
            borderRadius="md"
          >
            <Text fontSize="sm" fontWeight="500">
              {text(
                t,
                'phoneNumbers.bulk.selected',
                `${selectedIds.size} selected`,
                { count: selectedIds.size },
              )}
            </Text>
            <Button
              size="xs"
              colorPalette="brand"
              borderRadius="lg"
              onClick={() => setIsBulkModalOpen(true)}
              disabled={selectedIds.size > 25}
            >
              {text(t, 'phoneNumbers.bulk.updateStatus', 'Update Status')}
            </Button>
            <Button
              size="xs"
              variant="ghost"
              borderRadius="lg"
              onClick={() => setSelectedIds(new Set())}
            >
              {text(t, 'phoneNumbers.bulk.clearSelection', 'Clear')}
            </Button>
            {selectedIds.size > 25 && (
              <Text fontSize="xs" color="fg.error">
                {text(t, 'phoneNumbers.bulk.maxExceeded', 'Max 25 at a time')}
              </Text>
            )}
          </Box>
        )}
        <Box overflowX="auto" pb={4}>
          <Table.Root
            variant="outline"
            size="sm"
            borderRadius="lg"
            overflow="hidden"
          >
            <Table.Header>
              <Table.Row>
                {canBulkUpdate && (
                  <Table.ColumnHeader width="40px">
                    <Checkbox
                      size="sm"
                      checked={allSelected}
                      onCheckedChange={(e) => toggleSelectAll(!!e.checked)}
                      inputProps={{
                        'aria-label': text(
                          t,
                          'phoneNumbers.bulk.selectAll',
                          'Select all phone numbers',
                        ),
                      }}
                    />
                  </Table.ColumnHeader>
                )}
                <Table.ColumnHeader
                  cursor="pointer"
                  userSelect="none"
                  onClick={() => handleSort('name')}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {text(t, 'phoneNumbers.list.columnName', 'Name')}
                    <SortIcon field="name" sort={sort} />
                  </Box>
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  cursor="pointer"
                  userSelect="none"
                  onClick={() => handleSort('number')}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {text(t, 'phoneNumbers.list.columnNumber', 'Number')}
                    <SortIcon field="number" sort={sort} />
                  </Box>
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  cursor="pointer"
                  userSelect="none"
                  onClick={() => handleSort('call_endpoint')}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {text(t, 'phoneNumbers.list.columnExtension', 'Extension')}
                    <SortIcon field="call_endpoint" sort={sort} />
                  </Box>
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  cursor="pointer"
                  userSelect="none"
                  onClick={() => handleSort('caller_identification')}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {text(t, 'phoneNumbers.list.columnCallerId', 'Caller ID')}
                    <SortIcon field="caller_identification" sort={sort} />
                  </Box>
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  cursor="pointer"
                  userSelect="none"
                  onClick={() => handleSort('toll_free')}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {text(t, 'phoneNumbers.list.columnTollFree', 'Toll Free')}
                    <SortIcon field="toll_free" sort={sort} />
                  </Box>
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {text(t, 'phoneNumbers.list.columnSms', 'SMS')}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {text(t, 'phoneNumbers.list.columnProviders', 'Providers')}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {text(t, 'phoneNumbers.list.columnUpdated', 'Updated')}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {phoneNumbers.map((pn: PhoneNumberListItem) => (
                <PhoneNumberRow
                  key={pn.id}
                  pn={pn}
                  t={t}
                  showCheckbox={canBulkUpdate}
                  selected={selectedIds.has(pn.id)}
                  onSelectChange={(checked) => toggleSelect(pn.id, checked)}
                  onClick={
                    getDetailPath || onRowClick
                      ? () => handleRowClick(pn)
                      : undefined
                  }
                  onCopy={handleCopyNumber}
                />
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      </>
    )
  }

  return (
    <Box>
      <Box pb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="start">
          <Box>
            <Heading size="lg">
              {text(t, 'phoneNumbers.list.title', 'Phone Numbers')}
            </Heading>
            {isStaff && serverGroupLabel && (
              <Text fontSize="sm" color="fg.muted" mt={1}>
                {text(t, 'phoneNumbers.list.serverGroup', 'Server Group:')}{' '}
                {serverGroupLabel}
              </Text>
            )}
          </Box>
          <Box display="flex" gap={3} alignItems="center">
            <Input
              placeholder={text(
                t,
                'phoneNumbers.list.searchPlaceholder',
                'Search...',
              )}
              value={searchInput}
              onChange={handleSearchChange}
              size="sm"
              maxW="240px"
            />
            <Button
              size="sm"
              variant="outline"
              colorPalette={activeFilterCount > 0 ? 'brand' : undefined}
              borderRadius="lg"
              onClick={() => setShowFilters((v) => !v)}
            >
              {text(t, 'phoneNumbers.list.filterButton', 'Filters')}
              {activeFilterCount > 0 && (
                <Badge
                  size="xs"
                  colorPalette="brand"
                  variant="solid"
                  borderRadius="full"
                  ml={1}
                >
                  {activeFilterCount}
                </Badge>
              )}
              <Box
                as="span"
                display="inline-flex"
                transition="transform 0.2s"
                transform={showFilters ? 'rotate(180deg)' : 'rotate(0deg)'}
                ml={1}
              >
                <ChevronDown size={14} />
              </Box>
            </Button>
            {canCreate !== false && (
              <Button
                size="sm"
                colorPalette="brand"
                borderRadius="lg"
                onClick={() => setIsCreateOpen(true)}
              >
                {text(
                  t,
                  'phoneNumbers.list.createButton',
                  '+ Create Phone Number',
                )}
              </Button>
            )}
          </Box>
        </Box>

        {showFilters && (
          <FilterPanel
            filters={filters}
            onChange={handleFilterChange}
            statuses={statusOptions}
            providers={providerOptions}
            isStaff={isStaff}
            t={t}
          />
        )}
      </Box>
      <Box>
        <PaginationToolbar mb={4} />
        {renderContent()}
        <PaginationToolbar />
      </Box>

      <CreatePhoneNumberModal
        pbxId={pbxId}
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSave={handleCreate}
        onSaveSuccess={handleCreateSuccess}
        isSaving={createMutation.isPending}
        t={t}
        statuses={statusOptions}
        providers={providerOptions}
        extensions={extensionOptions}
        faxMachines={faxMachineOptions}
        integrations={integrationOptions}
        defaultCountryCode={defaultCountryCode}
        serverGroup={isStaff ? serverGroupLabel : undefined}
      />

      {canBulkUpdate && (
        <BulkStatusModal
          open={isBulkModalOpen}
          selectedCount={selectedIds.size}
          statuses={statusOptions}
          onClose={() => setIsBulkModalOpen(false)}
          onConfirm={handleBulkConfirm}
          isUpdating={bulkMutation.isPending}
          t={t}
        />
      )}

      <BulkUpdateResultsDialog
        result={bulkUpdateResult}
        onClose={() => {
          setBulkUpdateResult(null)
          setSelectedIds(new Set())
        }}
        t={t}
      />
    </Box>
  )
}
